import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgbNavChangeEvent } from '@ng-bootstrap/ng-bootstrap';
import { catchError, EMPTY, first, Observable, of, Subscription } from 'rxjs';
import { scrollToElement, unsubscribeAll } from 'src/app/common/app.helpers';
import AppValues from 'src/app/common/app.values';
import { OperationInfoService } from 'src/app/common/long-operations/operation-info.service';
import { PaginationResolver } from 'src/app/interfaces/pagination.interface';
import { TableCellOnActionEvent, UserTableConstructorInfo } from 'src/app/interfaces/tables.interfaces';
import { DialogActionTypes } from 'src/app/notifications/dialogs/dialogs.enum';
import { NotificationContextAttributesService } from 'src/app/notifications/notifications-context-attributes.service';
import {
  DeactivatedUsersCounters,
  DeactivationIncident,
  PaginatableDeactivationIncidents,
  ReactivationPetitionStatus,
} from 'src/app/swagger-api';
import { ManageProfileService } from '../../profile/manage-profile.service';
import { initialPaginationState } from '../../shared/social-initiative/social-initiative.helpers';
import { CellActionTypes } from '../../tables/components/cell-actions.type';
import { DeactivatedUsersService } from './deactivated-users.service';
import { DeclinedTableFactory } from './factory/declined-table-factory';
import { DisabledTableFactory } from './factory/disabled-table-factory';
import { ReactivationTableFactory } from './factory/reactivation-table-factory';

const TabType = {
  DISABLED_ACCOUNTS: 'disabled_accounts',
  REACTIVATION_REQUESTS: 'reactivation_requests',
  DECLINED_REQUESTS: 'declined_requests',
};

@Component({
  selector: 'app-deactivated-users',
  templateUrl: './deactivated-users.component.html',
  styleUrls: ['./deactivated-users.component.scss'],
})
export class DeactivatedUsersComponent implements OnInit, OnDestroy {
  public DISABLED_ACCOUNTS = `disabled_accounts`;
  public REACTIVATION_REQUESTS = `reactivation_requests`;
  public DECLINED_REQUESTS = `declined_requests`;

  // Resolvers

  public resolver: PaginationResolver = { state: initialPaginationState() };

  // Counters
  public disabledAccounts = 0;
  public reactivationRequests = 0;
  public declinedRequests = 0;

  // TableData
  public disabledTableData: UserTableConstructorInfo;
  public reactivationRequestTableData: UserTableConstructorInfo;
  public declinedRequestTableData: UserTableConstructorInfo;

  public deactivationIncident: DeactivationIncident[];

  public pageSize = AppValues.DEACTIVATION_PAGE_SIZE;
  protected subscriptions$: Subscription[] = [];
  public currentTab = TabType.DISABLED_ACCOUNTS;

  public isReactivationTab: boolean;

  // Scroll helpers
  private profileIdFragment: string;

  private ref = inject(ChangeDetectorRef);
  private deactivatedUsersService = inject(DeactivatedUsersService);
  private manageProfileService = inject(ManageProfileService);
  private operationInfoService = inject(OperationInfoService);
  private modalAttributesService = inject(NotificationContextAttributesService);
  private activatedRoute = inject(ActivatedRoute);

  public ngOnInit(): void {
    this.initializeResolvers();
    this.subscribeOnCounterChanges();
    this.checkActiveRoute();
  }

  public ngOnDestroy(): void {
    unsubscribeAll(this.subscriptions$);
  }

  public onTabClick($event: NgbNavChangeEvent<string>): void {
    this.resetPaginationControls();
    this.deactivatedUsersService.fetchRequestCounters();
    this.disabledTableData = null;
    this.reactivationRequestTableData = null;
    this.declinedRequestTableData = null;

    this.currentTab = $event.nextId;
    this.tabTypes[this.currentTab]();
  }

  public onTableAction(event: TableCellOnActionEvent): void {
    const actions: { [key: string]: () => void } = {
      [CellActionTypes.CONFIRM]: () => this.revertAccountReactivationPetition(event),
      [CellActionTypes.REJECT]: () => this.declineReactivationPetition(event),
      [CellActionTypes.REMOVE]: () => this.onReactivateAccount(event),
    };
    actions[event.type] && actions[event.type]();
  }

  public get fragment(): Observable<string | null> {
    return this.activatedRoute.fragment;
  }

  public scrollToIncident(fragment: string): void {
    scrollToElement(fragment);
  }

  private onReactivateAccount(event: TableCellOnActionEvent): void {
    this.operationInfoService.setupOperation(
      {
        successfulMessage: 'DIALOG_REACTIVATION_DESCRIPTION',
        confirmationDialog: {
          description: 'REACTIVATION_DESCRIPTION',
          title: 'REACTIVATION_TITLE',
          buttons: [
            this.modalAttributesService.createDialogButton('BTN_CANCEL_HINT'),
            this.modalAttributesService.createDialogButton(
              'BTN_REACTIVATION_HINT',
              'btn-primary',
              DialogActionTypes.CONFIRM_ACTION,
            ),
          ],
        },
      },
      () => {
        this.manageProfileService
          .reactivateAccount(event.actorId)
          .pipe(
            first(),
            catchError(() => EMPTY),
          )
          .subscribe(() => {
            this.onListUpdate();
          });
      },
    );
  }

  private revertAccountReactivationPetition(event: TableCellOnActionEvent): void {
    this.operationInfoService.setupOperation(
      {
        successfulMessage: 'DIALOG_REACTIVATION_DESCRIPTION',
        confirmationDialog: {
          description: 'REACTIVATION_DESCRIPTION',
          title: 'REACTIVATION_TITLE',
          buttons: [
            this.modalAttributesService.createDialogButton('BTN_CANCEL_HINT'),
            this.modalAttributesService.createDialogButton(
              'BTN_REACTIVATION_HINT',
              'btn-primary',
              DialogActionTypes.CONFIRM_ACTION,
            ),
          ],
        },
      },
      () => {
        this.manageProfileService
          .revertAccountReactivationPetition(this.getEmail(event.entityId))
          .pipe(
            first(),
            catchError(() => EMPTY),
          )
          .subscribe(() => {
            this.onListUpdate();
          });
      },
    );
  }

  private declineReactivationPetition(event: TableCellOnActionEvent): void {
    this.manageProfileService
      .confirmDeclineAccountReactivationPetition(this.getEmail(event.entityId))
      .pipe(
        first(),
        catchError(() => {
          this.getReactivationRequestList(this.calculateOffset(this.resolver), this.pageSize);
          return EMPTY;
        }),
      )
      .subscribe((dialogConfirmed: boolean) => {
        if (dialogConfirmed) this.onListUpdate();
      });
  }

  private onListUpdate(): void {
    if (this.resolver.state.pagination.itemsCount === 1 && this.resolver.state.pagination.currentPage > 1) {
      this.resolver.state.pagination.currentPage -= 1;

      this.ref.detectChanges();
    }

    this.tabTypes[this.currentTab]();
    this.deactivatedUsersService.fetchRequestCounters();
  }

  private tabTypes: { [key: string]: () => void } = {
    disabled_accounts: () => this.getDisabledAccounts(this.calculateOffset(this.resolver), this.pageSize),
    reactivation_requests: () => this.getReactivationRequestList(this.calculateOffset(this.resolver), this.pageSize),
    declined_requests: () => this.getDeclinedRequestList(this.calculateOffset(this.resolver), this.pageSize),
  };

  private subscribeOnCounterChanges(): void {
    this.subscriptions$.push(
      this.deactivatedUsersService.counterChanges.subscribe((counters: DeactivatedUsersCounters) => {
        this.disabledAccounts = counters.disabled_accounts;
        this.reactivationRequests = counters.reactivation_requests;
        this.declinedRequests = counters.declined_requests;
      }),
    );
  }

  private getEmail(id: string): string {
    return this.deactivationIncident.find((item: DeactivationIncident) => item.profile_view.user_id === id)
      .reactivation_petition.deactivated_user_email;
  }

  private getDisabledAccounts(offset: number, limit: number): void {
    this.subscriptions$.push(
      this.deactivatedUsersService
        .getDeactivationIncidents(null, offset, limit)
        .pipe(
          first(),
          catchError(() => {
            return of({ items: [], total_count: 0 });
          }),
        )
        .subscribe((data: PaginatableDeactivationIncidents) => {
          this.disabledTableData = new DisabledTableFactory().buildTableData(data.items);
          this.resolver.state.pagination.totalItemsCount = data.total_count;
          this.resolver.state.pagination.itemsCount = data.items.length;

          this.ref.detectChanges();
        }),
    );
  }

  private getReactivationRequestList(offset: number, limit: number): void {
    this.subscriptions$.push(
      this.deactivatedUsersService
        .getDeactivationIncidents(ReactivationPetitionStatus.OPEN, offset, limit)
        .pipe(
          first(),
          catchError(() => {
            return of({ items: [], total_count: 0 });
          }),
        )
        .subscribe((data: PaginatableDeactivationIncidents) => {
          this.reactivationRequestTableData = new ReactivationTableFactory().buildTableData(data.items);
          this.deactivationIncident = data.items;

          this.resolver.state.pagination.totalItemsCount = data.total_count;
          this.resolver.state.pagination.itemsCount = data.items.length;

          this.ref.detectChanges();

          // If a fragment is present, scroll to the element
          if (this.profileIdFragment) this.scrollToIncident(this.profileIdFragment);
        }),
    );
  }

  private getDeclinedRequestList(offset: number, limit: number): void {
    this.subscriptions$.push(
      this.deactivatedUsersService
        .getDeactivationIncidents(ReactivationPetitionStatus.REJECTED, offset, limit)
        .pipe(
          first(),
          catchError(() => {
            return of({ items: [], total_count: 0 });
          }),
        )
        .subscribe((data: PaginatableDeactivationIncidents) => {
          this.declinedRequestTableData = new DeclinedTableFactory().buildTableData(data.items);

          this.resolver.state.pagination.totalItemsCount = data.total_count;
          this.resolver.state.pagination.itemsCount = data.items.length;

          this.ref.detectChanges();
        }),
    );
  }

  private resetPaginationControls(): void {
    this.resolver.state = initialPaginationState();

    this.ref.detectChanges();
  }

  private calculateOffset(resolver: PaginationResolver): number {
    return (resolver.state.pagination.currentPage - 1) * this.pageSize;
  }

  private initializeResolvers(): void {
    const onPageChangesFactory = (resolver: PaginationResolver) => (page: number) => {
      resolver.state.pagination.currentPage = page;
      this.tabTypes[this.currentTab]();
    };

    this.resolver.onPageChanged = onPageChangesFactory(this.resolver);

    this.ref.detectChanges();
  }

  private checkActiveRoute(): void {
    this.fragment.subscribe((fragment) => {
      this.isReactivationTab = fragment?.includes(AppValues.reactivationTab);
      this.profileIdFragment = fragment?.split('/').length == 2 ? fragment?.split('/')[1] : null;
      this.deactivatedUsersService.fetchRequestCounters();
      if (this.isReactivationTab) {
        this.currentTab = TabType.REACTIVATION_REQUESTS;
        this.getReactivationRequestList(this.calculateOffset(this.resolver), this.pageSize);
      } else this.getDisabledAccounts(this.calculateOffset(this.resolver), this.pageSize);
    });
  }
}
